import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from 'store/hooks'
import { ModalType, openModal } from 'store/modals/modalSlice'
import { getIntegrationLogoNoSuffix } from 'lib/utils'
import axios from 'lib/axios'
import theme from 'theme'
import toast from 'react-hot-toast'
import { AvailableCPI } from '../../categoryDashboard.types'
import { AvailableCPICardWrapper } from './cpisSection.styles'
import ThemeButton from 'ui/components/buttons/primary/ThemeButton'
import { isActivateCpiHidden } from 'ui/components/modal/variants/activate-cpi/activateCpiStorage'

interface Props {
  cpi: AvailableCPI
  onActivated?: () => void | Promise<void>
}

const buildManifestUserInput = (manifestData: any) => {
  const userInputManifest = manifestData?.ui?.trend_chart?.user_input
  const visualization = userInputManifest?.visualization_param
  const formula = userInputManifest?.formula_params ?? []

  const sourceSections =
    formula.length > 0 && formula[0]?.sections?.length > 0 ? formula[0].sections : visualization?.sections ?? []

  const hasSections =
    (formula.length > 0 && formula[0]?.sections?.length > 0) || (visualization?.sections?.length ?? 0) > 1

  const formulaSections = hasSections
    ? sourceSections.map((s: any) => ({ name: s.name, value: Number(s.value ?? 0) }))
    : []

  const formulaParams = formula.map((n: any) => {
    const result: any = { name: n.name, sections: formulaSections }
    if (n.value != null) result.value = n.value
    return result
  })

  const identicalSectionValues = visualization?.identical_section_values ?? true
  const userInput: any = {
    visualization_param: {
      identical_section_values: identicalSectionValues
    }
  }

  if (identicalSectionValues) {
    userInput.visualization_param.identical_value = visualization?.identical_value ?? 0
  } else if (formulaSections.length > 0) {
    userInput.visualization_param.sections = formulaSections
  }

  if (formulaParams.length > 0) userInput.formula_params = formulaParams

  return userInput
}

const AvailableCPICard: FC<Props> = ({ cpi, onActivated }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const handleNavigate = () => {
    navigate(`/library/cpis/${cpi.cpiId.toLowerCase()}/trends`)
  }

  const activateCpi = async () => {
    try {
      // Fetch manifest from details endpoint for accurate is_v2 and default values
      const manifestRes = await axios.get(`/api/v3/cpis/${cpi.cpiName}/details`)
      const manifestData = manifestRes.data.data

      if (!manifestData) {
        toast.error('Failed to load CPI manifest')
        return
      }

      const isV2 = manifestData.is_v2
      const userInput = buildManifestUserInput(manifestData)

      if (isV2) {
        await axios.post(`/api/v4/metrics/${cpi.cpiName}/create?activate-metric`, {
          instance_names: cpi.configuredInstanceNames,
          metric_config: userInput
        })
      } else {
        await axios.post(`/api/v3/cpis/${cpi.cpiName}?activate-cpi`, {
          user_input: userInput,
          integration_ids: cpi.configuredIntegrationIds
        })
      }

      toast.success(`${cpi.cpiId} activated successfully`)
      // Fire-and-forget so the modal can close immediately alongside the toast;
      // awaiting the refetch would keep the modal open for the duration of the
      // network round trips.
      onActivated?.()
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to activate CPI'
      toast.error(message)
      console.error('Error activating CPI:', err)
    }
  }

  const handleActivate = () => {
    if (isActivateCpiHidden(cpi.cpiName)) {
      activateCpi()
      return
    }

    dispatch(
      openModal({
        type: ModalType.activateCpi,
        props: {
          cpiName: cpi.cpiName,
          cpiDisplayName: cpi.cpiId,
          activateCpiCallback: activateCpi
        }
      })
    )
  }

  return (
    <AvailableCPICardWrapper>
      <div className="card-header">
        <div className="cpi-chip" onClick={handleNavigate}>
          <span>{cpi.cpiId}</span>
        </div>
        <div className="cpi-name" onClick={handleNavigate}>
          {cpi.name}
        </div>
      </div>

      <div className="card-content">
        <div className="description-section">
          <span className="description-label">Description</span>
          <span className="description-text">{cpi.description}</span>
        </div>

        <div className="data-sources-section">
          <span className="data-sources-label">Potential Data Sources</span>
          <div className="data-sources-logos">
            {cpi.dataSourceLogos.map((logo, index) => (
              <img
                key={index}
                src={getIntegrationLogoNoSuffix(logo)}
                alt="data source"
                onError={(e) => {
                  ;(e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            ))}
          </div>
        </div>

        <ThemeButton
          themevariant="primary"
          shape="rectangular"
          onClick={handleActivate}
          sx={{
            width: '100%',
            background: `${theme.colors.btnTertiaryBg} !important`,
            color: `${theme.colors.cta} !important`,
            border: 'none !important',
            '&:hover': {
              background: `${theme.colors.btnTertiaryBg} !important`,
              opacity: 0.85
            }
          }}
        >
          Activate CPI
        </ThemeButton>
      </div>
    </AvailableCPICardWrapper>
  )
}

export default AvailableCPICard
